import { DateUrlParamsFilter } from '../../entities/filters/date-url-params-filter';
import { SimpleParamFilter } from '../../shared/ui/param-filters/simple-param-filter';
import { PAYORDER_GROUPS, PAYORDER_STATUSES } from '@sr/dto';
import { useBankAccountsDropdown } from '../../hooks/bank-accounts.hook';
import { ManagerUrlParamsFilter } from 'entities/filters/manager-url-params-filter';
import { FilterWrapper, ValueFormatterContext } from '../../shared/ui/filter/filter-wrapper';
import { Stack } from '@mui/material';
import { useManagersDropdownList } from 'features/user/user-picker/ui';
import { InputParamFilter } from 'shared/ui/param-filters/input-param-filter';

export const PayordersFilter = () => {
  const bankAccounts = useBankAccountsDropdown();
  const BANK_ACCOUNTS = bankAccounts.items.map((item) => ({ label: item.title, id: item.id }));
  const { items: managers } = useManagersDropdownList();

  const managerValueFormatter = ({ value }: ValueFormatterContext) => {
    const manager = managers.find((m) => String(m.id) === value);
    return manager ? `${manager.firstName} ${manager.secondName || ''}`.trim() : value;
  };

  const createOptionFormatter = (options: { id: number | string; label: string }[]) => {
    const map = new Map(options.map((item) => [String(item.id), item.label]));
    return ({ value }: ValueFormatterContext) => map.get(String(value)) ?? value;
  };

  const bankAccountFormatter = createOptionFormatter(BANK_ACCOUNTS);
  const statusFormatter = createOptionFormatter(PAYORDER_STATUSES);
  const groupFormatter = createOptionFormatter(PAYORDER_GROUPS);

  return (
    <FilterWrapper title="Фильтры бухгалтерии">
      <FilterWrapper.Item label="Менеджер" queryKeys="managerId" valueFormatter={managerValueFormatter}>
        <ManagerUrlParamsFilter queryParamName="managerId" />
      </FilterWrapper.Item>

      <FilterWrapper.Item label="Расчетный счет" queryKeys="bankAccountId" valueFormatter={bankAccountFormatter}>
        <SimpleParamFilter queryParamName={'bankAccountId'} label="Рассчетный счет" items={BANK_ACCOUNTS} />
      </FilterWrapper.Item>

      <FilterWrapper.Item label="Статус" queryKeys="statusId" valueFormatter={statusFormatter}>
        <SimpleParamFilter queryParamName="statusId" items={PAYORDER_STATUSES} label="Статус" />
      </FilterWrapper.Item>

      <FilterWrapper.Item label="Группа" queryKeys="groupIds" valueFormatter={groupFormatter}>
        <SimpleParamFilter queryParamName="groupIds" items={PAYORDER_GROUPS} label="Группа" />
      </FilterWrapper.Item>

      <FilterWrapper.Item
        label="Дата платежа"
        queryKeys={['fromDate', 'toDate']}
        summaryFormatter={({ searchParams }) => {
          const from = searchParams.get('fromDate');
          const to = searchParams.get('toDate');
          if (!from && !to) return 'Дата платежа';
          if (from && to) return `Платеж: ${from} — ${to}`;
          return `Платеж: ${from || to}`;
        }}
      >
        <Stack spacing={1.5}>
          <DateUrlParamsFilter queryParamName={'fromDate'} label="Дата платежа с " />
          <DateUrlParamsFilter queryParamName={'toDate'} label="Дата платежа по" />
        </Stack>
      </FilterWrapper.Item>

      <FilterWrapper.Item
        label="Сумма"
        queryKeys={['fromPrice', 'toPrice']}
        summaryFormatter={({ searchParams }) => {
          const from = searchParams.get('fromPrice');
          const to = searchParams.get('toPrice');
          if (!from && !to) return 'Сумма';
          if (from && to) return `Сумма: ${from} — ${to}`;
          return `Сумма: ${from || to}`;
        }}
      >
        <Stack spacing={1.5}>
          <InputParamFilter queryParamName={'fromPrice'} label="Сумма платежа от " />
          <InputParamFilter queryParamName={'toPrice'} label="Сумма платежа до" />
        </Stack>
      </FilterWrapper.Item>
    </FilterWrapper>
  );
};
